import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PlusIcon, MinusIcon } from "@heroicons/react/24/outline";
import { completePhoto, deleteImage, archiveBatch, fetchPhotoIds, emptyEnvironment } from "../api";

// Hooks
import { useLocalStorage, useLocalStorageNumber, useLocalStorageBool } from "../hooks/useLocalStorage";
import { usePhotoStats } from "../hooks/usePhotoStats";
import { usePhotoList, filterPhotos, mapSortToApi } from "../hooks/usePhotoList";
import { useImageLoader } from "../hooks/useImageLoader";
import { useInfiniteScroll } from "../hooks/useInfiniteScroll";
import { usePermissions } from "../hooks";
import { useUserMatches } from "../features/overview/hooks";
import { STORAGE_KEYS } from "../utils/constants";

// Components
import StatsPills from "../components/overview/StatsPills";
import PhotoCard from "../components/overview/PhotoCard";
import SearchBar from "../components/overview/SearchBar";
import TimeFilter from "../components/overview/TimeFilter";
import MultiSelectBar from "../components/overview/MultiSelectBar";
import MatchStackCard from "../features/overview/components/MatchStackCard";
import MatchBreadcrumb from "../features/overview/components/MatchBreadcrumb";
import LoadingOverlay from "../shared/components/ui/LoadingOverlay";
import EmptyEnvironmentModal from "../components/overview/EmptyEnvironmentModal";

// Constants
const PAGE_SIZE = 100;

export default function Overview({ onNavigate, onTopBadge }) {
  // Permissions
  const {
    canApprovePhotos,
    canArchivePhotos,
    canDeletePhotos,
    canEmptyEnvironment,
    getClickTarget,
  } = usePermissions();

  // Persisted preferences
  const [refreshMs, setRefreshMs] = useLocalStorageNumber("matrix-refresh-ms", 10000, { min: 0 });
  // Full-frame (viz) is the default for all roles; the toggle was removed 2026-04-22.
  const [showViz] = useLocalStorageBool("matrix-show-viz", true);
  const [sortOrder, setSortOrder] = useLocalStorage("matrix-sort-order", "oldest");
  const [activeFilters, setActiveFilters] = useLocalStorage("matrix-active-filters", []);
  const [timeRange, setTimeRange] = useLocalStorage("matrix-time-range", null);
  const [showTimeSlider, setShowTimeSlider] = useLocalStorageBool("matrix-show-time-slider", false);
  const [matchGrouping, setMatchGrouping] = useLocalStorage(STORAGE_KEYS.MATCH_GROUPING, "none");

  // Local state
  const [search, setSearch] = useState("");
  const [multiMode, setMultiMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [markingDone, setMarkingDone] = useState(false);
  const [deletingPhotos, setDeletingPhotos] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [archivingPhotos, setArchivingPhotos] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [selectingAll, setSelectingAll] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [showEmptyEnvModal, setShowEmptyEnvModal] = useState(false);

  // Bulk operation state for loading overlay
  const [bulkOperation, setBulkOperation] = useState({
    active: false,
    type: null, // 'delete' | 'archive' | 'markDone'
    total: 0,
    completed: 0,
  });
  const bulkCancelRef = useRef(false);

  // Layout refs
  const statsRef = useRef(null);
  const gridRef = useRef(null);

  // Custom hooks
  const imageLoader = useImageLoader();
  const { stats, totals, metrics, rateLabel, ingestionStats } = usePhotoStats(refreshMs);
  const { matches, liveMatches, isMatchLive } = useUserMatches();

  const {
    photos,
    loading,
    loadingMore,
    hasMore,
    error,
    loadMore,
    refresh: refreshPhotos,
    totalCount: serverTotalCount,
  } = usePhotoList({
    activeFilters,
    sortOrder,
    search,
    refreshMs,
    imageErrorsRef: imageLoader.errorsRef,
  });

  const { sentinelRef } = useInfiniteScroll({
    hasMore,
    loading: loadingMore,
    onLoadMore: loadMore,
    enabled: photos.length > 0,
  });

  // Computed values
  const listPhotos = useMemo(
    () => filterPhotos(photos, activeFilters, timeRange),
    [photos, activeFilters, timeRange]
  );

  const hasTimeData = useMemo(
    () => photos.some((p) => p.capture_date),
    [photos]
  );

  // Clear selectedMatch when switching grouping modes
  useEffect(() => {
    setSelectedMatch(null);
  }, [matchGrouping]);

  // Filter photos for "my_match" mode - only show photos from live matches
  const matchFilteredPhotos = useMemo(() => {
    if (matchGrouping === 'my_match') {
      if (liveMatches.length === 0) {
        return listPhotos; // Show all if no live matches
      }
      const liveMatchIds = new Set(liveMatches.map((m) => m.id));
      return listPhotos.filter((p) => p.match && liveMatchIds.has(p.match));
    }
    if (matchGrouping === 'by_match' && selectedMatch !== null) {
      // Filter by selected match (null match ID = unassigned)
      if (selectedMatch === 'unassigned') {
        return listPhotos.filter((p) => !p.match);
      }
      return listPhotos.filter((p) => p.match === selectedMatch);
    }
    return listPhotos;
  }, [matchGrouping, listPhotos, liveMatches, selectedMatch]);

  // Group photos by match when in "by_match" mode
  const photosByMatch = useMemo(() => {
    if (matchGrouping !== 'by_match' || selectedMatch !== null) return null;

    const grouped = {};
    listPhotos.forEach((photo) => {
      const matchId = photo.match || 'unassigned';
      if (!grouped[matchId]) {
        grouped[matchId] = [];
      }
      grouped[matchId].push(photo);
    });
    return grouped;
  }, [matchGrouping, listPhotos, selectedMatch]);

  // Get match details by ID for displaying in stack cards
  const getMatchById = useCallback(
    (matchId) => matches.find((m) => m.id === matchId),
    [matches]
  );

  // Get selected match details for breadcrumb
  const selectedMatchDetails = useMemo(() => {
    if (selectedMatch === 'unassigned') return null;
    return getMatchById(selectedMatch);
  }, [selectedMatch, getMatchById]);

  // URL helpers
  const fileBase = useMemo(() => {
    if (typeof window === "undefined") return "";
    const api = import.meta.env.VITE_OPERATOR_API || "http://localhost:8000/api";
    return api.replace(/\/api\/?$/, "");
  }, []);

  const resolveUrl = useCallback(
    (url) => {
      if (!url) return "";
      if (/^https?:\/\//i.test(url)) return url;
      const sep = url.startsWith("/") ? "" : "/";
      return `${fileBase}${sep}${url}`;
    },
    [fileBase]
  );

  const withCache = useCallback((url, cacheKey) => {
    if (!url) return "";
    if (!cacheKey) return url;
    const join = url.includes("?") ? "&" : "?";
    return `${url}${join}v=${encodeURIComponent(cacheKey)}`;
  }, []);

  const cacheKeyFor = useCallback((p) => `${p.status}-${p.__cache || p.updated_at || "0"}`, []);


  // Update top badge with rate
  useEffect(() => {
    if (typeof onTopBadge === "function") {
      onTopBadge(rateLabel || "");
      return () => onTopBadge("");
    }
    return undefined;
  }, [rateLabel, onTopBadge]);

  // Filter handlers
  const toggleFilter = useCallback((key) => {
    setActiveFilters((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  }, [setActiveFilters]);

  const clearFilters = useCallback(() => setActiveFilters([]), [setActiveFilters]);
  const clearTimeRange = useCallback(() => setTimeRange(null), [setTimeRange]);

  // Multi-select handlers
  const toggleMultiMode = useCallback(() => {
    setMultiMode((prev) => {
      if (prev) setSelectedIds(new Set());
      return !prev;
    });
  }, []);

  const toggleSelection = useCallback((imageId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(imageId)) {
        next.delete(imageId);
      } else {
        next.add(imageId);
      }
      return next;
    });
  }, []);

  const selectAll = useCallback(async () => {
    // If more photos exist than currently loaded, fetch all matching IDs from server
    if (serverTotalCount > listPhotos.length) {
      setSelectingAll(true);
      try {
        // Build params matching usePhotoList filter logic
        const params = {};
        if (activeFilters.includes("unlabeled")) {
          params.has_unlabeled_faces = true;
        }
        if (activeFilters.includes("done")) {
          params.status = "done,exported";
        }
        if (activeFilters.includes("attention")) {
          params.attention = true;
        }
        if (search) {
          params.q = search;
        }
        if (sortOrder) {
          params.ordering = mapSortToApi(sortOrder);
        }

        const { ids } = await fetchPhotoIds(params);
        setSelectedIds(new Set(ids));
      } catch (e) {
        console.error("Failed to fetch all IDs:", e);
        // Fallback to just loaded photos
        setSelectedIds(new Set(listPhotos.map((p) => p.image_id)));
      } finally {
        setSelectingAll(false);
      }
    } else {
      // All photos are loaded, use them directly
      setSelectedIds(new Set(listPhotos.map((p) => p.image_id)));
    }
  }, [listPhotos, serverTotalCount, activeFilters, search, sortOrder]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  // Batch action handlers - process in chunks to avoid browser connection limits
  const BATCH_SIZE = 10;

  const handleMarkDone = useCallback(async () => {
    if (selectedIds.size === 0 || markingDone) return;
    setMarkingDone(true);
    bulkCancelRef.current = false;

    const ids = Array.from(selectedIds);
    const total = ids.length;

    // Show loading overlay for bulk operations (3+ items)
    if (total >= 3) {
      setBulkOperation({ active: true, type: 'markDone', total, completed: 0 });
    }

    try {
      let completed = 0;
      for (let i = 0; i < ids.length; i += BATCH_SIZE) {
        if (bulkCancelRef.current) break;
        const batch = ids.slice(i, i + BATCH_SIZE);
        await Promise.all(batch.map((id) => completePhoto(id)));
        completed += batch.length;
        if (total >= 3) {
          setBulkOperation((prev) => ({ ...prev, completed }));
        }
      }

      if (!bulkCancelRef.current) {
        setSelectedIds(new Set());
        setMultiMode(false);
      }
      refreshPhotos();
    } catch (e) {
      console.error("Failed to mark done:", e);
    } finally {
      setMarkingDone(false);
      setBulkOperation({ active: false, type: null, total: 0, completed: 0 });
    }
  }, [selectedIds, markingDone, refreshPhotos]);

  const handleDelete = useCallback(() => {
    if (selectedIds.size === 0 || deletingPhotos) return;
    setShowDeleteConfirm(true);
  }, [selectedIds, deletingPhotos]);

  const confirmDelete = useCallback(async () => {
    setShowDeleteConfirm(false);
    setDeletingPhotos(true);
    bulkCancelRef.current = false;

    const ids = Array.from(selectedIds);
    const total = ids.length;

    // Show loading overlay for bulk operations (3+ items)
    if (total >= 3) {
      setBulkOperation({ active: true, type: 'delete', total, completed: 0 });
    }

    try {
      let completed = 0;
      const processedIds = [];
      for (let i = 0; i < ids.length; i += BATCH_SIZE) {
        if (bulkCancelRef.current) break;
        const batch = ids.slice(i, i + BATCH_SIZE);
        await Promise.all(batch.map((id) => deleteImage(id)));
        processedIds.push(...batch);
        completed += batch.length;
        if (total >= 3) {
          setBulkOperation((prev) => ({ ...prev, completed }));
        }
      }

      if (bulkCancelRef.current) {
        // Keep remaining items selected for retry
        const processedSet = new Set(processedIds);
        setSelectedIds((prev) => new Set([...prev].filter((id) => !processedSet.has(id))));
      } else {
        setSelectedIds(new Set());
        setMultiMode(false);
      }
      refreshPhotos();
    } catch (e) {
      console.error("Failed to delete:", e);
    } finally {
      setDeletingPhotos(false);
      setBulkOperation({ active: false, type: null, total: 0, completed: 0 });
    }
  }, [selectedIds, refreshPhotos]);

  const handleArchive = useCallback(() => {
    if (selectedIds.size === 0 || archivingPhotos) return;
    setShowArchiveConfirm(true);
  }, [selectedIds, archivingPhotos]);

  const confirmArchive = useCallback(async () => {
    setShowArchiveConfirm(false);
    setArchivingPhotos(true);
    bulkCancelRef.current = false;

    const ids = Array.from(selectedIds);
    const total = ids.length;

    // Show loading overlay for bulk operations (3+ items)
    // Archive is a single API call, so we show indeterminate progress
    if (total >= 3) {
      setBulkOperation({ active: true, type: 'archive', total, completed: 0 });
    }

    try {
      await archiveBatch(ids);
      setSelectedIds(new Set());
      setMultiMode(false);
      refreshPhotos();
    } catch (e) {
      console.error("Failed to archive:", e);
    } finally {
      setArchivingPhotos(false);
      setBulkOperation({ active: false, type: null, total: 0, completed: 0 });
    }
  }, [selectedIds, refreshPhotos]);

  // Helper function to get operation message for loading overlay
  const getOperationMessage = useCallback((type) => {
    switch (type) {
      case 'delete':
        return 'Deleting photos...';
      case 'archive':
        return 'Archiving photos...';
      case 'markDone':
        return 'Marking photos as done...';
      default:
        return 'Processing...';
    }
  }, []);

  // Cancel handler for bulk operations
  const handleCancelBulkOperation = useCallback(() => {
    bulkCancelRef.current = true;
  }, []);

  // Empty environment handler
  const handleEmptyEnvironment = useCallback(async ({ force = false } = {}) => {
    try {
      await emptyEnvironment({ force });
      setShowEmptyEnvModal(false);
      setMultiMode(false);
      setSelectedIds(new Set());
      refreshPhotos();
    } catch (e) {
      console.error('Failed to empty environment:', e);
      throw e; // Let modal handle the error display
    }
  }, [refreshPhotos]);

  return (
    <div className="page page-overview">
      <div className="overview-header">
        <div className="filters">
          {multiMode ? (
            <MultiSelectBar
              selectedCount={selectedIds.size}
              loadedCount={listPhotos.length}
              totalCount={serverTotalCount}
              hasMore={hasMore}
              onCancel={toggleMultiMode}
              onMarkDone={handleMarkDone}
              onArchive={handleArchive}
              onDelete={handleDelete}
              onSelectAll={selectAll}
              onClearSelection={clearSelection}
              markingDone={markingDone}
              archivingPhotos={archivingPhotos}
              deletingPhotos={deletingPhotos}
              selectingAll={selectingAll}
              showArchive={activeFilters.includes("done")}
              canMarkDone={canApprovePhotos}
              canArchive={canArchivePhotos}
              canDelete={canDeletePhotos}
              canEmptyEnvironment={canEmptyEnvironment}
              onEmptyEnvironment={() => setShowEmptyEnvModal(true)}
            />
          ) : (
            <SearchBar
              search={search}
              onSearch={setSearch}
              sortOrder={sortOrder}
              onSort={setSortOrder}
              refreshMs={refreshMs}
              onRefreshChange={setRefreshMs}
              hasActiveFilters={activeFilters.length > 0 || !!timeRange}
              onClearFilters={() => { clearFilters(); clearTimeRange(); }}
              hasTimeData={hasTimeData}
              showTimeSlider={showTimeSlider}
              onToggleTimeSlider={() => setShowTimeSlider((v) => !v)}
              matchGrouping={matchGrouping}
              onMatchGroupingChange={setMatchGrouping}
            />
          )}
          <button
            className={`chip ${multiMode ? "multi-active" : ""}`}
            onClick={toggleMultiMode}
            title={multiMode ? "Exit multi-select mode" : "Enter multi-select mode"}
          >
            {multiMode ? <MinusIcon className="icon-sm" style={{ marginRight: 4 }} /> : <PlusIcon className="icon-sm" style={{ marginRight: 4 }} />}
            Multi
          </button>
        </div>

        {/* TimeFilter V2 - shows when photos have capture timestamps */}
        {showTimeSlider && (
          <TimeFilter
            photos={photos}
            timeRange={timeRange}
            onChange={setTimeRange}
            onClear={clearTimeRange}
          />
        )}

        <div ref={statsRef}>
          <StatsPills
            totals={totals}
            activeFilters={activeFilters}
            onToggle={toggleFilter}
            onClear={clearFilters}
            metrics={metrics}
            ingestionStats={ingestionStats}
          />
        </div>
      </div>

      {error && <div className="error-text">{error}</div>}

      {/* Breadcrumb when viewing a specific match in by_match mode */}
      {matchGrouping === 'by_match' && selectedMatch !== null && (
        <MatchBreadcrumb
          match={selectedMatchDetails}
          photoCount={matchFilteredPhotos.length}
          isLive={selectedMatch !== 'unassigned' && isMatchLive(selectedMatch)}
          onBack={() => setSelectedMatch(null)}
        />
      )}

      {/* My Match mode - show message if no live matches */}
      {matchGrouping === 'my_match' && liveMatches.length === 0 && (
        <div
          style={{
            padding: '12px 16px',
            margin: '0 0 16px',
            background: 'rgba(102, 183, 255, 0.1)',
            border: '1px solid rgba(102, 183, 255, 0.3)',
            borderRadius: 8,
            color: '#66b7ff',
            fontSize: 14,
          }}
        >
          No live matches at the moment. Showing all photos.
        </div>
      )}

      {matchFilteredPhotos.length === 0 && !loading ? (
        <div className="empty-state">
          <div className="empty-message">Waiting for photos...</div>
          <div className="empty-sub">New photos will appear automatically.</div>
        </div>
      ) : matchGrouping === 'by_match' && photosByMatch && selectedMatch === null ? (
        /* Show match stacks when in by_match mode and no match selected */
        <div className="thumb-grid" ref={gridRef} style={{ '--card-min-width': '280px' }}>
          {Object.entries(photosByMatch).map(([matchId, matchPhotos]) => {
            const match = matchId === 'unassigned' ? null : getMatchById(matchId);
            const statusCounts = {
              pending: matchPhotos.filter((p) => p.status === 'pending' || p.status === 'processing').length,
              done: matchPhotos.filter((p) => p.status === 'done' || p.status === 'exported').length,
            };
            const previewUrls = matchPhotos.slice(0, 3).map((p) =>
              resolveUrl(withCache(p.thumb_url, cacheKeyFor(p)))
            );
            return (
              <MatchStackCard
                key={matchId}
                match={match}
                photoCount={matchPhotos.length}
                statusCounts={statusCounts}
                isLive={matchId !== 'unassigned' && isMatchLive(matchId)}
                isUnassigned={matchId === 'unassigned'}
                previewUrls={previewUrls}
                onClick={() => setSelectedMatch(matchId)}
              />
            );
          })}
        </div>
      ) : (
        <div className="thumb-grid" ref={gridRef}>
          {matchFilteredPhotos.map((p) => (
            <PhotoCard
              key={p.image_id}
              photo={p}
              showViz={showViz}
              multiMode={multiMode}
              isSelected={selectedIds.has(p.image_id)}
              onSelect={toggleSelection}
              onNavigate={onNavigate}
              imageLoader={imageLoader}
              activeFilters={activeFilters}
              resolveUrl={resolveUrl}
              withCache={withCache}
              cacheKeyFor={cacheKeyFor}
              getClickTarget={getClickTarget}
            />
          ))}

          {/* Sentinel for infinite scroll */}
          <div
            ref={sentinelRef}
            style={{ height: 20, width: "100%", gridColumn: "1 / -1" }}
          />

          {loadingMore && (
            <div
              className="load-more-indicator"
              style={{ gridColumn: "1 / -1", textAlign: "center", padding: "20px 0" }}
            >
              <span style={{ color: "#66ffb7", opacity: 0.8 }}>Loading more...</span>
            </div>
          )}

          {!hasMore && matchFilteredPhotos.length > 0 && matchFilteredPhotos.length >= PAGE_SIZE && (
            <div
              style={{
                gridColumn: "1 / -1",
                textAlign: "center",
                padding: "16px 0",
                color: "#666",
                fontSize: 13,
              }}
            >
              Showing all {matchFilteredPhotos.length} photos
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
          onClick={() => setShowDeleteConfirm(false)}
        >
          <div
            style={{
              background: "#1a1a1a",
              border: "1px solid #333",
              borderRadius: 12,
              padding: "24px 32px",
              maxWidth: 400,
              textAlign: "center",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontSize: 32, marginBottom: 16, color: "#dc3545", fontWeight: "bold" }}>DELETE</div>
            <h3 style={{ margin: "0 0 12px", color: "#fff" }}>
              Delete {selectedIds.size} photo{selectedIds.size !== 1 ? "s" : ""}?
            </h3>
            <p style={{ color: "#888", margin: "0 0 24px", fontSize: 14 }}>
              This action cannot be undone. The photos will be permanently removed.
            </p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                style={{
                  padding: "10px 24px",
                  background: "#333",
                  border: "none",
                  borderRadius: 6,
                  color: "#fff",
                  cursor: "pointer",
                  fontSize: 14,
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                style={{
                  padding: "10px 24px",
                  background: "#dc3545",
                  border: "none",
                  borderRadius: 6,
                  color: "#fff",
                  cursor: "pointer",
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Archive Confirmation Modal */}
      {showArchiveConfirm && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
          onClick={() => setShowArchiveConfirm(false)}
        >
          <div
            style={{
              background: "#1a1a1a",
              border: "1px solid #333",
              borderRadius: 12,
              padding: "24px 32px",
              maxWidth: 400,
              textAlign: "center",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontSize: 32, marginBottom: 16, color: "#66b7ff", fontWeight: "bold" }}>ARCHIVE</div>
            <h3 style={{ margin: "0 0 12px", color: "#fff" }}>
              Archive {selectedIds.size} photo{selectedIds.size !== 1 ? "s" : ""}?
            </h3>
            <p style={{ color: "#888", margin: "0 0 24px", fontSize: 14 }}>
              Photos will be archived to a zip file. Original files will be removed
              from central storage, but thumbnails and metadata will remain for
              viewing.
            </p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
              <button
                onClick={() => setShowArchiveConfirm(false)}
                style={{
                  padding: "10px 24px",
                  background: "#333",
                  border: "none",
                  borderRadius: 6,
                  color: "#fff",
                  cursor: "pointer",
                  fontSize: 14,
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmArchive}
                style={{
                  padding: "10px 24px",
                  background: "#a29bff",
                  border: "none",
                  borderRadius: 6,
                  color: "#000",
                  cursor: "pointer",
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                Archive
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Empty Environment Modal */}
      {showEmptyEnvModal && (
        <EmptyEnvironmentModal
          isOpen={showEmptyEnvModal}
          onClose={() => setShowEmptyEnvModal(false)}
          onConfirm={handleEmptyEnvironment}
          stats={stats}
        />
      )}

      {/* Bulk Operation Loading Overlay */}
      <LoadingOverlay
        isVisible={bulkOperation.active}
        message={getOperationMessage(bulkOperation.type)}
        completed={bulkOperation.completed}
        total={bulkOperation.total}
        onCancel={bulkOperation.type !== 'archive' ? handleCancelBulkOperation : undefined}
      />
    </div>
  );
}
